import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useExamContext } from "@/contexts/ExamContext";
import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Filter, Play, BookOpen, AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

type FilterStatus = "all" | "unanswered" | "incorrect" | "bookmarked";

const sb = supabase as any;

const QuizBuilder = () => {
  const { user } = useAuth();
  const { selectedExamSlug } = useExamContext();
  const navigate = useNavigate();

  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [difficulties, setDifficulties] = useState<number[]>([1, 2, 3, 4, 5]);
  const [status, setStatus] = useState<FilterStatus>("all");
  const [count, setCount] = useState<number>(10);
  const [mode, setMode] = useState<"tutor" | "test">("tutor");

  // Topics for selected exam
  const { data: examLabel } = useQuery({
    queryKey: ["builder-exam-label", selectedExamSlug],
    enabled: !!selectedExamSlug,
    queryFn: async () => {
      const { data } = await sb.from("exam_labels").select("id").eq("slug", selectedExamSlug).maybeSingle();
      return data?.id ?? null;
    },
  });

  const { data: topics = [] } = useQuery({
    queryKey: ["builder-topics", examLabel],
    queryFn: async () => {
      if (!examLabel) {
        const { data } = await sb.from("topics").select("id, name").order("name");
        return data ?? [];
      }
      const { data: labelMcqs } = await sb
        .from("mcq_exam_labels")
        .select("mcq_id")
        .eq("label_id", examLabel);
      const ids = (labelMcqs ?? []).map((r: any) => r.mcq_id);
      if (!ids.length) return [];
      const { data: mcqs } = await sb.from("mcqs").select("topic_id").in("id", ids);
      const topicIds = [...new Set((mcqs ?? []).map((m: any) => m.topic_id).filter(Boolean))];
      if (!topicIds.length) return [];
      const { data } = await sb.from("topics").select("id, name").in("id", topicIds).order("name");
      return data ?? [];
    },
  });

  // Live count of matching MCQs
  const { data: matchingCount = 0, isFetching: countLoading } = useQuery({
    queryKey: ["builder-count", user?.id, examLabel, selectedTopics, difficulties, status],
    enabled: !!user,
    queryFn: async () => {
      let q = sb.from("mcqs").select("id", { count: "exact", head: true }).eq("lifecycle_status", "active");
      if (selectedTopics.length) q = q.in("topic_id", selectedTopics);
      if (difficulties.length && difficulties.length < 5) q = q.in("difficulty", difficulties);
      if (examLabel) {
        const { data: labelMcqs } = await sb
          .from("mcq_exam_labels")
          .select("mcq_id")
          .eq("label_id", examLabel);
        const ids = (labelMcqs ?? []).map((r: any) => r.mcq_id);
        if (!ids.length) return 0;
        q = q.in("id", ids);
      }

      // Status filtering
      if (status === "bookmarked") {
        const { data: bms } = await sb.from("user_bookmarks").select("mcq_id").eq("user_id", user!.id);
        const ids = (bms ?? []).map((b: any) => b.mcq_id);
        if (!ids.length) return 0;
        q = q.in("id", ids);
      } else if (status === "unanswered") {
        const { data: atts } = await sb.from("attempts").select("mcq_id").eq("user_id", user!.id);
        const answeredIds = [...new Set((atts ?? []).map((a: any) => a.mcq_id))];
        if (answeredIds.length) {
          const inList = `(${answeredIds.map((id: string) => `"${id}"`).join(",")})`;
          q = q.not("id", "in", inList);
        }
      } else if (status === "incorrect") {
        const { data: atts } = await sb
          .from("attempts")
          .select("mcq_id")
          .eq("user_id", user!.id)
          .eq("is_correct", false);
        const ids = [...new Set((atts ?? []).map((a: any) => a.mcq_id))];
        if (!ids.length) return 0;
        q = q.in("id", ids);
      }

      const { count } = await q;
      return count ?? 0;
    },
  });

  const effectiveCount = Math.min(count, matchingCount);

  const startQuiz = () => {
    if (matchingCount === 0) {
      toast({ title: "No matching questions", description: "Loosen your filters", variant: "destructive" });
      return;
    }
    const params = new URLSearchParams();
    if (selectedTopics.length === 1) params.set("topic", selectedTopics[0]);
    if (difficulties.length === 1) params.set("difficulty", String(difficulties[0]));
    params.set("count", String(effectiveCount));
    params.set("mode", mode);
    if (status !== "all") params.set("filter", status);
    navigate(`/practice?${params.toString()}`);
  };

  const toggleTopic = (id: string) =>
    setSelectedTopics((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));
  const toggleDifficulty = (d: number) =>
    setDifficulties((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <SEO title="Custom Quiz Builder" description="Build a personalized practice quiz" path="/quiz-builder" />

      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-primary" />
          Custom Quiz Builder
        </h1>
        <p className="text-muted-foreground mt-1">Tailor a practice session to your goals.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          {/* Topics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="h-4 w-4" /> Topics
                <Badge variant="secondary" className="ml-auto">{selectedTopics.length || "All"}</Badge>
              </CardTitle>
              <CardDescription>Leave empty for all topics</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48 pr-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {topics.map((t: any) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={selectedTopics.includes(t.id)}
                        onCheckedChange={() => toggleTopic(t.id)}
                      />
                      <span>{t.name}</span>
                    </label>
                  ))}
                  {topics.length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-2">No topics for selected exam.</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Difficulty */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Difficulty
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((d) => (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={difficulties.includes(d) ? "default" : "outline"}
                    onClick={() => toggleDifficulty(d)}
                  >
                    Level {d}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Question pool</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={status} onValueChange={(v) => setStatus(v as FilterStatus)}>
                {[
                  { v: "all", l: "All available questions" },
                  { v: "unanswered", l: "Unanswered only" },
                  { v: "incorrect", l: "Previously incorrect" },
                  { v: "bookmarked", l: "Bookmarked only" },
                ].map((opt) => (
                  <div key={opt.v} className="flex items-center gap-2">
                    <RadioGroupItem value={opt.v} id={`pool-${opt.v}`} />
                    <Label htmlFor={`pool-${opt.v}`} className="cursor-pointer">{opt.l}</Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Mode */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mode</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={mode} onValueChange={(v) => setMode(v as "tutor" | "test")}>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="tutor" id="mode-tutor" className="mt-1" />
                  <Label htmlFor="mode-tutor" className="cursor-pointer">
                    <div className="font-medium">Tutor</div>
                    <div className="text-xs text-muted-foreground">See explanation immediately after each answer.</div>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="test" id="mode-test" className="mt-1" />
                  <Label htmlFor="mode-test" className="cursor-pointer">
                    <div className="font-medium">Test</div>
                    <div className="text-xs text-muted-foreground">Answers and explanations at the end only.</div>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        </div>

        {/* Summary sidebar */}
        <div className="space-y-4">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-base">Your quiz</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Number of questions</Label>
                  <span className="font-mono text-sm">{count}</span>
                </div>
                <Slider
                  value={[count]}
                  onValueChange={(v) => setCount(v[0])}
                  min={5}
                  max={Math.max(50, matchingCount)}
                  step={5}
                />
              </div>

              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Available:</span>
                  <span className="font-semibold">{countLoading ? "…" : matchingCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Will load:</span>
                  <span className="font-semibold">{effectiveCount}</span>
                </div>
              </div>

              {matchingCount === 0 && !countLoading && (
                <div className="flex items-start gap-2 text-xs text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>No questions match. Try removing filters.</span>
                </div>
              )}

              <Button
                className="w-full bg-gradient-medical"
                size="lg"
                onClick={startQuiz}
                disabled={matchingCount === 0 || countLoading}
              >
                <Play className="h-4 w-4 mr-2" />
                Start Quiz
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuizBuilder;
